import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Home, LogOut, KeyRound, MessagesSquare, X as XIcon } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const LOGO_URL = "/dfa-logo.svg";

const ParentLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [pwdOpen, setPwdOpen] = useState(false);
  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const doLogout = () => { logout(); navigate("/login"); };
  const changePwd = async () => {
    if (!pwd.current || !pwd.next) return toast.error("Enter both passwords");
    if (pwd.next.length < 8) return toast.error("New password must be at least 8 characters");
    if (pwd.next !== pwd.confirm) return toast.error("New password and confirmation don't match");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: pwd.current, new_password: pwd.next });
      toast.success("Password updated");
      setPwdOpen(false); setPwd({ current: "", next: "", confirm: "" });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FDFBF7] via-white to-[#EEF3E5]">
      {/* Top bar */}
      <header className="sticky top-0 z-40 backdrop-blur-lg bg-white/70 border-b border-[#1B2C5A]/8 shadow-[0_1px_0_rgba(27,44,90,0.04)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-3">
          <NavLink to="/parent" className="flex items-center gap-2.5 group" data-testid="pl-home-link">
            <div className="w-9 h-9 rounded-xl bg-white p-0.5 ring-1 ring-[#9DCD3F]/40 grid place-items-center overflow-hidden shrink-0">
              <img src={LOGO_URL} alt="DFA" className="w-full h-full object-contain" />
            </div>
            <div className="hidden sm:block">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#9DCD3F]">Darussalam</div>
              <div className="font-display text-sm text-[#1B2C5A] leading-tight">Parent Portal</div>
            </div>
          </NavLink>
          <div className="ml-auto flex items-center gap-2">
            <NavLink to="/parent" end className={({ isActive }) => "hidden sm:inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full transition " + (isActive ? "bg-[#9DCD3F]/20 text-[#1B2C5A] font-medium" : "text-[#475569] hover:text-[#1B2C5A]")} data-testid="pl-children-tab">
              <Home className="w-3.5 h-3.5" /> My Children
            </NavLink>
            <NavLink to="/parent/engagements" className={({ isActive }) => "inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full transition " + (isActive ? "bg-[#9DCD3F]/20 text-[#1B2C5A] font-medium" : "text-[#475569] hover:text-[#1B2C5A]")}>
              <MessagesSquare className="w-3.5 h-3.5" /> Engagements
            </NavLink>
            <div className="flex items-center gap-2 pl-3 border-l border-[#E2E8F0]">
              <div className="w-8 h-8 rounded-full bg-[#1B2C5A] text-[#9DCD3F] grid place-items-center text-xs font-display" data-testid="pl-avatar">
                {user?.avatar_initials || (user?.name?.[0] || "P")}
              </div>
              <div className="hidden sm:block leading-tight">
                <div className="text-xs font-medium text-[#1B2C5A] truncate max-w-[140px]">{user?.name}</div>
                <div className="text-[10px] text-[#9DCD3F] uppercase tracking-wider">Parent</div>
              </div>
              <button onClick={() => setPwdOpen(true)} className="ml-1 p-2 rounded-full hover:bg-[#9DCD3F]/20 text-[#475569] hover:text-[#1B2C5A] transition" title="Change my password" data-testid="pl-change-password">
                <KeyRound className="w-4 h-4" />
              </button>
              <button onClick={doLogout} className="ml-1 p-2 rounded-full hover:bg-red-50 text-[#475569] hover:text-red-700 transition" title="Sign out" data-testid="pl-logout">
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6"><Outlet /></main>
      <footer className="max-w-7xl mx-auto px-4 sm:px-6 pb-6 text-[11px] text-[#475569]/60 text-center">
        Darussalam Foundation Academy · Knowledge & Action
      </footer>

      {/* Change password dialog */}
      <Dialog open={pwdOpen} onOpenChange={setPwdOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display text-[#1B2C5A] flex items-center gap-2">
              <KeyRound className="w-4 h-4 text-[#9DCD3F]" /> Change Password
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-[#1B2C5A]">Current Password</Label><Input type="password" value={pwd.current} onChange={(e) => setPwd({ ...pwd, current: e.target.value })} data-testid="pwd-current" /></div>
            <div><Label className="text-xs text-[#1B2C5A]">New Password (min 8 chars)</Label><Input type="password" value={pwd.next} onChange={(e) => setPwd({ ...pwd, next: e.target.value })} data-testid="pwd-next" /></div>
            <div><Label className="text-xs text-[#1B2C5A]">Confirm New Password</Label><Input type="password" value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} data-testid="pwd-confirm" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwdOpen(false)} disabled={busy}>Cancel</Button>
            <Button onClick={changePwd} disabled={busy} className="bg-[#1B2C5A] hover:bg-[#14224A] text-white" data-testid="pwd-save">
              {busy ? "Updating…" : "Update Password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ParentLayout;
